import React from 'react';
import { useTranslation } from 'react-i18next';
import { pln, int } from './format';
import { KRAJ, jestPolska } from '../../kraj';
import useKosztZestawu from '../../hooks/useKosztZestawu';

/**
 * Rozpisany koszt zestawu — jeden komponent dla karty w asystencie i dla ekranu
 * potwierdzenia.
 *
 * DLACZEGO WSPÓLNY: na obu ekranach stoi ta sama kwota tego samego zestawu. Dwie
 * kopie tego rachunku znaczyłyby, że po podmianie produktu w kroku drugim suma
 * potrafi się nie zgadzać z tą z kroku pierwszego — a to jedyna liczba, którą
 * użytkownik naprawdę porównuje.
 *
 * PODZIAŁ NA WARSTWY WIARYGODNOŚCI jest celowy i widoczny: cena produktów
 * i dostawa pochodzą z ofert sklepów, konstrukcja i montaż to NASZE szacunki
 * z cenników. Dlatego te dwie pozycje mają w nazwie słowo „szacowana" i pokazujemy
 * przy nich, z czego liczyliśmy (rodzaj połaci, stawka za kWp).
 *
 * ═══ TRYB FIRMOWY ═══
 * Zalogowana firma instalatorska ma własne stawki montażu, marżę i stawkę VAT
 * (ustawienia konta → VoltValue Projekt). Wtedy:
 *   - stawka montażu idzie z JEJ tabeli, nie z naszego cennika poglądowego,
 *   - do kwoty netto doliczana jest marża, a całość przeliczana na jej stawkę VAT,
 *   - przy „pokazuj tylko cenę końcową" (domyślnie WŁĄCZONE) rozbicie znika.
 *
 * MARŻY NIE POKAZUJEMY W ŻADNYM WIERSZU — nawet przy widocznym rozbiciu. Ten sam
 * ekran bywa drukowany do PDF-a dla klienta końcowego, a osobna linia „marża"
 * na takim dokumencie to najgorsza możliwa niespodzianka. Firma widzi swoje
 * stawki tam, gdzie je ustawia.
 *
 * Dla gościa i konta prywatnego nie zmienia się NIC: `useUstawieniaWyceny`
 * zwraca wtedy `null`, a rachunek leci dokładnie tak jak dotąd.
 */
const Wiersz = ({ etykieta, dopisek, kwota }) => (
  <div className="flex justify-between gap-3 text-sm mb-1">
    <span className="text-gray-600">
      {etykieta}
      {dopisek && <span className="text-[10px] text-gray-400 whitespace-nowrap"> ({dopisek})</span>}
    </span>
    <span className="font-medium text-gray-800 whitespace-nowrap">{pln(kwota)}</span>
  </div>
);

const PodsumowanieKosztow = ({
  produktyZl,
  bmsZl = null,
  dostawaZl = null,
  liczbaDostaw = null,
  liczbaPalet = null,
  liczbaSprzedawcow = null,
  kwp,
  mocModuluW,
  konstrukcja,
  montazWliczony = true,
  // Magazyn zestawu (runda 6): pojemność i cena magazynów, do montażu i marży za magazyn z ustawień firmy.
  kwh = null,
  magazynZl = 0,
  klasa = 'rounded-xl bg-gray-50 p-4',
  /** `true` = sama kwota, bez rozbicia. Dokument dla klienta zawsze tak. */
  tylkoKwota = null,
}) => {
  const {
    wycena, wKonstrukcja, stawkaZlZaKwp, zlMontaz, razemZl, przeliczone, stawkaVat,
    marzaZl, zlMontazMagazynu, stawkaMagazynuZlZaKwh,
  } = useKosztZestawu({ produktyZl, bmsZl, dostawaZl, kwp, mocModuluW, konstrukcja, montazWliczony, kwh, magazynZl });
  const { t } = useTranslation('zestaw');

  // Ustawienie konta rozstrzyga TYLKO tam, gdzie wołający nie ma zdania.
  // Karta zestawu wymusza rozbicie (`false`), dokument dla klienta wymusza samą
  // kwotę (`true`) — mają różnych odbiorców, więc nie może o tym decydować
  // jeden przełącznik dla obu.
  const tylkoKoncowa = tylkoKwota != null
    ? tylkoKwota
    : Boolean(wycena && wycena.only_final_price);

  const opisDostaw = [
    liczbaDostaw != null
      ? t('podsumowanie_kosztow.dostawy', { count: Math.abs(Number(liczbaDostaw)), liczba: int(liczbaDostaw) })
      : null,
    liczbaPalet
      ? t('podsumowanie_kosztow.palety', { count: Math.abs(Number(liczbaPalet)), liczba: int(liczbaPalet) })
      : null,
  ].filter(Boolean).join(', ');

  // Nazwa połaci z lib/konstrukcja jest polska; w innym języku bierzemy ją ze słownika.
  const etykietaKonstrukcji = wKonstrukcja
    ? t(`podsumowanie_kosztow.konstrukcje.${wKonstrukcja.konstrukcja}`, { defaultValue: wKonstrukcja.etykieta })
    : null;

  // Poza Polską stawka to polska stawka razy kurs (`zZlotych`), więc bywa ułamkiem
  // w rodzaju 1298.0000000000002; pokazujemy ją zaokrągloną. W Polsce bez zmian.
  const stawkaWyswietlana = jestPolska ? stawkaZlZaKwp : int(stawkaZlZaKwp);

  return (
    <div className={klasa}>
      {tylkoKoncowa ? (
        /* Widok dla klienta końcowego: jedna kwota za całość. Wymieniamy, co
           wchodzi w skład, ale BEZ kwot — inaczej to samo rozbicie wróciłoby
           tylnymi drzwiami. */
        <p className="text-sm text-gray-600 leading-6 mb-2">
          {montazWliczony && zlMontaz != null
            ? t('podsumowanie_kosztow.cena_obejmuje_z_montazem')
            : t('podsumowanie_kosztow.cena_obejmuje')}
        </p>
      ) : (
        <>
          <Wiersz etykieta={t('podsumowanie_kosztow.cena_produktow')} kwota={produktyZl} />
          {bmsZl != null && <Wiersz etykieta={t('podsumowanie_kosztow.bms')} kwota={bmsZl} />}
          {wKonstrukcja && (
            <Wiersz etykieta={t('podsumowanie_kosztow.konstrukcja')}
                    dopisek={etykietaKonstrukcji.toLowerCase()} kwota={wKonstrukcja.razemZl} />
          )}
          {zlMontaz != null && (
            <Wiersz etykieta={t('podsumowanie_kosztow.montaz')}
                    dopisek={t('podsumowanie_kosztow.stawka_za_kwp', { stawka: stawkaWyswietlana, waluta: KRAJ.symbol })}
                    kwota={zlMontaz} />
          )}
          {zlMontazMagazynu != null && (
            <Wiersz etykieta={t('podsumowanie_kosztow.montaz_magazynu')}
                    dopisek={t('podsumowanie_kosztow.stawka_za_kwh', { stawka: int(stawkaMagazynuZlZaKwh), waluta: KRAJ.symbol })}
                    kwota={zlMontazMagazynu} />
          )}
          {dostawaZl != null && (
            /* Zawsze „Szacowane dostawy": koszt liczymy z wagi i liczby palet, a nie
               z cennika sklepu, więc nawet gdy silnik nie oznaczy tego jako szacunku,
               to nadal szacunek. Liczba mnoga, bo zestaw prawie zawsze idzie z kilku
               sklepów i w kilku przesyłkach. */
            <Wiersz etykieta={t('podsumowanie_kosztow.dostawy_etykieta')} dopisek={opisDostaw || null} kwota={dostawaZl} />
          )}
          {/* Marża NA KOŃCU rozbicia i tylko tutaj — kartę ogląda firma.
              Dokument dla klienta pokazuje samą kwotę końcową, w której marża
              jest już zawarta, ale jako osobna linia nigdy się tam nie pojawia. */}
          {marzaZl > 0 && (
            <Wiersz etykieta={t('podsumowanie_kosztow.marza')} dopisek={t('podsumowanie_kosztow.marza_dopisek')}
                    kwota={Math.round(marzaZl)} />
          )}
        </>
      )}

      <div className="flex justify-between items-baseline gap-3 pt-2 mt-2 border-t border-gray-200">
        <span className="font-semibold text-gray-900">
          {tylkoKoncowa ? t('podsumowanie_kosztow.cena_koncowa') : t('podsumowanie_kosztow.laczny_koszt')}
        </span>
        <span className="text-2xl font-extrabold text-blue-600 whitespace-nowrap">{pln(razemZl)}</span>
      </div>

      {/* Rozbicie VAT tylko wtedy, gdy firma ustawiła stawkę INNĄ niż źródłowa.
          Przy równych stawkach ta linijka nie niesie żadnej informacji, a na
          dokumencie dla klienta zajmowałaby miejsce tuż obok kwoty, na którą
          klient faktycznie patrzy. */}
      {przeliczone && stawkaVat != null && (
        <p className="text-[11px] text-gray-500 mt-1 text-right">
          {t('podsumowanie_kosztow.netto_vat', { kwota: pln(Math.round(przeliczone.nettoZl)), stawka: stawkaVat })}
        </p>
      )}

      {liczbaSprzedawcow != null && !tylkoKoncowa && (
        <p className="text-[11px] text-gray-400 mt-1 text-right">
          {t('podsumowanie_kosztow.sprzedawcy', { count: Math.abs(Number(liczbaSprzedawcow)), liczba: int(liczbaSprzedawcow) })}
        </p>
      )}
    </div>
  );
};

export default PodsumowanieKosztow;
